import type { CatalogDTO, CommandDraft, CommandResult, DeviceDTO, FloorDTO, OrderDTO, SessionDTO, TableDTO } from "@bb/protocol";
import { Api, ApiError } from "./api.ts";
import { Outbox, type OutboxEntry, type OutboxFailure } from "./outbox.ts";
import { kv } from "./storage.ts";
import { uuid } from "./uuid.ts";
import { SyncEngine, type ConnectionState } from "./sync.ts";

export interface PosSnapshot {
  ready: boolean;
  device: DeviceDTO | null;
  session: SessionDTO | null;
  catalog: CatalogDTO | null;
  floor: FloorDTO | null;
  orders: OrderDTO[];
  connection: ConnectionState;
  printing: import("@bb/protocol").PrintingStatus | null;
  /** Komendy zapisane na tablecie, jeszcze nieprzyjęte przez serwer. */
  pending: readonly OutboxEntry[];
  failures: OutboxFailure[];
  outboxNetworkDown: boolean;
  needsLogin: boolean;
}

export type SendOptions = { wait?: boolean };

/** Stan tabletu dla interfejsu: jedno źródło prawdy, odczyt przez useSyncExternalStore. */
export class PosClient {
  readonly api: Api;
  private deviceToken: string | null = null;
  private session: SessionDTO | null = null;
  private device: DeviceDTO | null = null;
  private catalog: CatalogDTO | null = null;
  private floor: FloorDTO | null = null;
  private sync: SyncEngine;
  private outbox: Outbox;
  private listeners = new Set<() => void>();
  private snap: PosSnapshot;
  private ready = false;
  private needsLogin = false;

  constructor(baseUrl = "") {
    this.api = new Api(baseUrl, () => ({ deviceToken: this.deviceToken, sessionToken: this.session?.token ?? null }));
    this.sync = new SyncEngine(this.api, () => this.deviceToken);
    this.outbox = new Outbox(this.api, {
      onAccepted: (r) => {
        if (this.sync.state !== "online" && r.seq) void this.sync.catchUp().catch(() => undefined);
      },
      onAuthRequired: () => {
        this.needsLogin = true;
        this.emit();
      },
    });
    this.sync.subscribe(() => this.emit());
    this.sync.onCatalogChanged = () => void this.refreshReferenceData().catch(() => undefined);
    this.outbox.subscribe(() => this.emit());
    this.snap = this.buildSnapshot();
  }

  /* ─── cykl życia ─── */

  async init(): Promise<void> {
    this.deviceToken = (await kv.get<string>("deviceToken")) ?? null;
    this.device = (await kv.get<DeviceDTO>("device")) ?? null;
    const stored = await kv.get<SessionDTO>("session");
    if (stored && new Date(stored.expiresAt) > new Date()) this.session = stored;
    // Ostatni znany katalog i sala — tablet pokaże menu także bez sieci.
    this.catalog = (await kv.get<CatalogDTO>("catalog")) ?? null;
    this.floor = (await kv.get<FloorDTO>("floor")) ?? null;
    await this.outbox.load();
    if (this.deviceToken) await this.startWork();
    this.ready = true;
    this.emit();
  }

  private async startWork(): Promise<void> {
    await this.refreshReferenceData().catch(() => undefined);
    await this.sync.start();
  }

  async refreshReferenceData(): Promise<void> {
    const [device, catalog, floor] = await Promise.all([
      this.api.get<DeviceDTO>("/api/v1/device"),
      this.api.get<CatalogDTO>("/api/v1/catalog"),
      this.api.get<FloorDTO>("/api/v1/floor"),
    ]).catch(async (e) => {
      if (e instanceof ApiError && e.status === 401) await this.unpair();
      throw e;
    });
    Object.assign(this, { device, catalog, floor });
    await Promise.all([kv.set("device", device), kv.set("catalog", catalog), kv.set("floor", floor)]);
    this.emit();
  }

  async pair(code: string): Promise<void> {
    const { token, device } = await this.api.post<{ token: string; device: DeviceDTO }>("/api/v1/devices/pair", { code });
    this.deviceToken = token;
    this.device = device;
    await kv.set("deviceToken", token);
    await kv.set("device", device);
    await this.startWork();
    this.emit();
  }

  async unpair(): Promise<void> {
    this.sync.stop();
    this.deviceToken = null;
    this.device = null;
    this.session = null;
    await Promise.all(["deviceToken", "device", "session", "catalog", "floor"].map((k) => kv.del(k)));
    this.emit();
  }

  async login(pin: string): Promise<SessionDTO> {
    const session = await this.api.post<SessionDTO>("/api/v1/auth/pin", { pin });
    this.session = session;
    this.needsLogin = false;
    await kv.set("session", session);
    this.outbox.resume();
    this.emit();
    return session;
  }

  /** Blokada ekranu. Sesja na serwerze zostaje — zakolejkowane komendy nadal pójdą jako ta osoba. */
  async lock(): Promise<void> {
    this.session = null;
    await kv.del("session");
    this.emit();
  }

  /** Stolik tymczasowy („+” na sali) — od razu odświeża salę na tym urządzeniu (inne dostają sygnał z serwera). */
  async createTemporaryTable(areaId: string, name: string): Promise<TableDTO> {
    const table = await this.api.post<TableDTO>("/api/v1/tables/temporary", { areaId, name });
    await this.refreshReferenceData().catch(() => undefined);
    return table;
  }

  async deleteTemporaryTable(id: string): Promise<void> {
    await this.api.request("DELETE", `/api/v1/tables/temporary/${id}`);
    await this.refreshReferenceData().catch(() => undefined);
  }

  async listStaff(): Promise<{ id: string; name: string; role: string }[]> {
    return this.api.get("/api/v1/staff");
  }

  async orderHistory(orderId: string): Promise<{ seq: number; type: string; details: any; at: string; staff: string; device: string }[]> {
    return this.api.get(`/api/v1/orders/${orderId}/history`);
  }

  /* ─── komendy ─── */

  /**
   * Wysyła komendę przez trwały outbox. Bez `wait` wraca od razu po zapisie na tablecie
   * (interfejs pokazuje „na tablecie”, potem „przyjęte”). Z `wait` czeka na odpowiedź serwera.
   */
  async send(command: CommandDraft, opts: SendOptions = {}): Promise<CommandResult | null> {
    const cmd = { ...command, commandId: command.commandId ?? uuid(), createdAt: new Date().toISOString() } as OutboxEntry["command"];
    const done = this.outbox.enqueue(cmd, this.session?.token ?? null);
    return opts.wait ? done : null;
  }

  dismissFailure(commandId: string): void {
    this.outbox.dismissFailure(commandId);
  }

  retryNow(): void {
    this.outbox.resume();
  }

  /* ─── stan dla React ─── */

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): PosSnapshot => this.snap;

  private buildSnapshot(): PosSnapshot {
    return {
      ready: this.ready,
      device: this.device,
      session: this.session,
      catalog: this.catalog,
      floor: this.floor,
      orders: [...this.sync.orders.values()],
      connection: this.sync.state,
      printing: this.sync.printing,
      pending: [...this.outbox.pending],
      failures: [...this.outbox.failures],
      outboxNetworkDown: this.outbox.networkDown,
      needsLogin: this.needsLogin,
    };
  }

  private emit(): void {
    this.snap = this.buildSnapshot();
    for (const fn of this.listeners) fn();
  }
}
